/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      rule => which user can have access (ACL)
                      breadcrumb => Add breadcrumb to specific page
                      pageTitle => Display title besides breadcrumb
                    }
==========================================================================================*/


import Vue from 'vue'
import Router from 'vue-router'
import store from '@/store/store'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior () {
    return { x: 0, y: 0 }
  },
  routes: [

    {
    // =============================================================================
    // MAIN LAYOUT ROUTES
    // =============================================================================
      path: '',
      component: () => import('./layouts/main/Main.vue'),
      children: [
        // =============================================================================
        // Theme Routes
        // =============================================================================
        {
          path: '/',
          redirect: '/dashboard'
        },
        {
          path: '/dashboard',
          name: 'dashboard-analytics',
          component: () => import('./views/DashboardAnalytics.vue'),
          meta: {
            rule: 'rootAndTerminal'
          }
        },

        {
          path: '/municipalities',
          name: 'municipalities',
          component: () => import('@/views/municipalities/MunicipalitieList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Municipalities', active: true }
            ],
            pageTitle: 'Municipalities',
            rule: 'root'
          }
        },
        {
          path: '/terminals',
          name: 'terminals',
          component: () => import('@/views/terminals/Terminals.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Terminallar', active: true }
            ],
            pageTitle: 'Terminallar',
            rule: 'root'
          }
        },
        {
          path: '/group-payment-statistics',
          name: 'group-payment-statistics',
          component: () => import('@/views/statistics/group/Group.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Statistika' },
              { title: 'Qrup ödəniş statistikası', active: true }
            ],
            pageTitle: 'Qrup ödəniş statistikası',
            rule: 'root'
          }
        },
        {
          path: '/statistics',
          name: 'statistics',
          component: () => import('@/views/statistics/group/Group.vue'),
          meta: {
            rule: 'root'
          }
        },
        {
          path: '/details-payment-statistics',
          name: 'details-payment-statistics',
          component: () => import('@/views/statistics/details/Details.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Statistika' },
              { title: 'Ətraflı ödəniş statistikası', active: true }
            ],
            pageTitle: 'Ətraflı ödəniş statistikası',
            rule: 'root'
          }
        },

        {
          path: '/terminal-payment',
          name: 'terminal-payment',
          component: () => import('@/views/statistics/terminal_payments/TerminalPayments.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Statistika' },
              { title: 'Ödənişlər', active: true }
            ],
            pageTitle: 'Ödənişlər',
            rule: 'terminal'
          }
        },
        {
          path: '/pay',
          name: 'payments',
          component: () => import('@/views/payments/payments/payments.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Ödənişin qəbulu', active: true }
            ],
            pageTitle: 'Ödənişin qəbulu',
            rule: 'terminal'
          }
        },
        {
          path: '/invoice',
          name: 'invoice',
          component: () => import('@/views/payments/Invoice.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Çek dublikatının çapı', active: true }
            ],
            pageTitle: 'Çek dublikatının çapı',
            rule: 'terminal'
          }
        }

      ]
    },
    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: '',
      component: () => import('@/layouts/full-page/FullPage.vue'),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================

        {
          path: '/login',
          name: 'Login',
          component: () => import('@/views/pages/login/Login.vue'),
          meta: {
            rule: 'rootAndTerminal'
          }
        },
        {
          path: '/forgot-password',
          name: 'page-forgot-password',
          component: () => import('@/views/pages/ForgotPassword.vue'),
          meta: {
            rule: 'terminal'
          }
        },
        {
          path: '/reset-password',
          name: 'page-reset-password',
          component: () => import('@/views/pages/ResetPassword.vue'),
          meta: {
            rule: 'terminal'
          }
        },
        {
          path: '/lock-screen',
          name: 'page-lock-screen',
          component: () => import('@/views/pages/LockScreen.vue'),
          meta: {
            rule: 'terminal'
          }
        },
        {
          path: '/comingsoon',
          name: 'page-coming-soon',
          component: () => import('@/views/pages/ComingSoon.vue'),
          meta: {
            rule: 'terminal'
          }
        },
        {
          path: '/error-404',
          name: 'page-error-404',
          component: () => import('@/views/pages/Error404.vue'),
          meta: {
            rule: 'terminal'
          }
        },
        {
          path: '/error-500',
          name: 'page-error-500',
          component: () => import('@/views/pages/Error500.vue'),
          meta: {
            rule: 'terminal'
          }
        },
        {
          path: '/not-authorized',
          name: 'page-not-authorized',
          component: () => import('@/views/pages/NotAuthorized.vue'),
          meta: {
            rule: 'terminal'
          }
        },
        {
          path: '/maintenance',
          name: 'page-maintenance',
          component: () => import('@/views/pages/Maintenance.vue'),
          meta: {
            rule: 'terminal'
          }
        }
      ]
    },
    // Redirect to 404 page, if no match found
    {
      path: '*',
      redirect: '/error-404'
    }
  ]
})

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }
})


router.beforeEach((to, from, next) => {
  if (to.name !== 'Login' && !store.getters['auth/loggedIn']) { next({ name: 'Login' }) } else if (to.name === 'Login' && store.getters['auth/loggedIn']) {
    next('/')
  } else {
    next()
  }
})

// router.beforeEach((to, from, next) => {
//   firebase.auth().onAuthStateChanged(() => {
//
//     // get firebase current user
//     const firebaseCurrentUser = firebase.auth().currentUser
//
//     // if (
//     //     to.path === "/pages/login" ||
//     //     to.path === "/pages/forgot-password" ||
//     //     to.path === "/pages/error-404" ||
//     //     to.path === "/pages/error-500" ||
//     //     to.path === "/pages/register" ||
//     //     to.path === "/callback" ||
//     //     to.path === "/pages/comingsoon" ||
//     //     (auth.isAuthenticated() || firebaseCurrentUser)
//     // ) {
//     //     return next();
//     // }
//
//     // If auth required, check login. If login fails redirect to login page
//     if (to.meta.authRequired) {
//       if (!(auth.isAuthenticated() || firebaseCurrentUser)) {
//         router.push({ path: '/pages/login', query: { to: to.path } })
//       }
//     }
//
//     return next()
//     // Specify the current path as the customState parameter, meaning it
//     // will be returned to the application after auth
//     // auth.login({ target: to.path });
//
//   })
//
// })

export default router
